import type { TeamsStatus } from '@arcnet/contracts';
import { Badge } from '../../components/ui/Primitives.js';

type Seat = TeamsStatus['seats'][number];

/**
 * A person's Teams status in words, from Microsoft Graph's availability and activity (docs/44;
 * owner, 21 October 2026: "they appear offline but the pbx still sends calls to teams ... we need to
 * know the teams status"). Green only for Available: a status, never decoration.
 */
export function teamsPresenceLabel(p: Seat['presence']): { label: string; tone: 'ok' | 'warn' | 'danger' | 'muted' } {
  if (!p) return { label: 'Teams: unknown', tone: 'muted' };
  if (p.activity === 'InACall' || p.activity === 'InAConferenceCall') return { label: 'Teams: in a call', tone: 'warn' };
  switch (p.availability) {
    case 'Available': case 'AvailableIdle': return { label: 'Teams: available', tone: 'ok' };
    case 'Busy': case 'BusyIdle': return { label: 'Teams: busy', tone: 'warn' };
    case 'DoNotDisturb': return { label: 'Teams: do not disturb', tone: 'danger' };
    case 'BeRightBack': case 'Away': return { label: 'Teams: away', tone: 'muted' };
    case 'Offline': return { label: 'Teams: signed out', tone: 'muted' };
    default: return { label: 'Teams: unknown', tone: 'muted' };
  }
}

export function TeamsPresenceBadge({ seat }: { seat: Seat | undefined }) {
  if (!seat) return null;
  const { label, tone } = teamsPresenceLabel(seat.presence);
  return <Badge tone={tone}>{label}</Badge>;
}

/**
 * Why a Teams seat may not take a call, in the order to fix it; empty when every known check passes.
 * Unknown checks (Microsoft not connected yet, the edge's record not readable) are not reported as
 * failures -- the call is still offered to Teams.
 */
export function teamsProblems(seat: Seat, connected: boolean): string[] {
  const out: string[] = [];
  if (!seat.checks.enabled) out.push('Ringing in Teams is switched off.');
  if (seat.checks.trunk_up === false) out.push('Microsoft is not answering ArcNet’s Teams trunk right now.');
  if (!seat.checks.sign_in_set) out.push('No Teams sign-in is set for this extension.');
  if (connected && seat.checks.user_found === false) out.push('Microsoft 365 has no user with that sign-in.');
  if (seat.checks.signed_in === false) out.push('Not signed in to Teams anywhere: a call rings nobody.');
  return out;
}

/**
 * A Teams seat's status in the extension list's Status column, the way a phone's is shown there
 * (ExtensionPresence PresenceCell): a dotted badge and a short detail (owner, 21 October 2026: "the
 * teams signed out can go in the status field like a phone would").
 */
export function TeamsStatusCell({ presence, connected }: { presence: Seat['presence'] | undefined; connected: boolean }) {
  const { label, tone } = connected
    ? teamsPresenceLabel(presence ?? null)
    : { label: 'Teams', tone: 'muted' as const };
  return (
    <div className="arc-row" style={{ gap: 7 }}>
      <span style={{ flex: '0 0 auto' }}>
        <Badge tone={tone}><span className="arc-dot" aria-hidden="true" />{label.replace(/^Teams: /, '')}</Badge>
      </span>
      <span className="arc-subtle arc-truncate" style={{ fontSize: 11.5, minWidth: 0 }}>
        {connected ? 'in Teams' : 'rings in Teams'}
      </span>
    </div>
  );
}
